import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useEffect, useState } from "react";
import { analyzeSelfie } from "@/lib/analyze.functions";
import { getStripeEnvironment } from "@/lib/stripe";
import { supabase } from "@/integrations/supabase/client";
import { Header } from "@/components/Header";
import { TipItem } from "@/components/TipItem";
import { GdprConsentModal, getStoredConsent, type GdprConsent } from "@/components/GdprConsentModal";
import { toast } from "sonner";
import { useT, useDocumentTitleKey, useLocale } from "@/i18n/locale-context";
import { prepareImage, formatBytesShort, type PrepareProgress } from "@/lib/image-prepare";
import { useJurisdiction } from "@/hooks/useJurisdiction";
import { NotesAndActionsField, type UserAction } from "@/components/NotesAndActionsField";

export const Route = createFileRoute("/quick")({
  head: () => ({ meta: [{ title: "Analyse express — Glow Up Coach" }] }),
  component: QuickPage,
});

const GOAL_IDS = ["skin", "weight", "muscle", "antiaging"] as const;
type Goal = (typeof GOAL_IDS)[number];

type Metric = { label: string; score: number; delta: string };
type Tip = { title: string; detail: string; category?: string; horizon?: string; frequency?: string; disclaimer?: string };
type Result = { summary: string; score?: number | null; metrics: Metric[]; tips: Tip[] };

type Snapshot = {
  imageUrl: string | null;
  createdAt: string;
  summary: string | null;
  score: number | null;
};

function QuickPage() {
  useDocumentTitleKey("quick.head.title");
  const t = useT();
  const { locale } = useLocale();
  const { jurisdiction } = useJurisdiction();
  const nav = useNavigate();
  const analyze = useServerFn(analyzeSelfie);

  const [authed, setAuthed] = useState(false);
  const [goal, setGoal] = useState<Goal>("skin");
  const [preview, setPreview] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<Result | null>(null);
  const [previous, setPrevious] = useState<Snapshot | null>(null);
  const [consent, setConsent] = useState<GdprConsent | null>(null);
  const [showConsent, setShowConsent] = useState(false);
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [uploadProgress, setUploadProgress] = useState<{ phase: PrepareProgress["phase"] | "sending"; percent: number } | null>(null);
  const [userNotes, setUserNotes] = useState("");
  const [userActions, setUserActions] = useState<UserAction[]>([]);

  useEffect(() => { setConsent(getStoredConsent()); }, []);

  useEffect(() => {
    supabase.auth.getSession().then(async ({ data }) => {
      if (!data.session) { nav({ to: "/login" }); return; }
      setAuthed(true);
      const { data: rows } = await supabase
        .from("analyses")
        .select("created_at, summary, score")
        .order("created_at", { ascending: false })
        .limit(1);
      const r = rows?.[0];
      if (r) {
        setPrevious({
          imageUrl: null,
          createdAt: r.created_at ?? new Date().toISOString(),
          summary: r.summary ?? null,
          score: r.score,
        });
      }
    });
  }, [nav]);

  async function onFile(file: File) {
    if (file.size > 45 * 1024 * 1024) { toast.error(t("app.toast.image_too_large")); return; }
    const current = consent ?? getStoredConsent();
    if (!current) { setPendingFile(file); setShowConsent(true); return; }
    setResult(null);
    setLoading(true);
    setUploadProgress({ phase: "reading", percent: 0 });
    try {
      const prepared = await prepareImage(file, undefined, (p) => setUploadProgress(p));
      setPreview(prepared.dataUrl);
      if (prepared.recompressed && prepared.compressedBytes < prepared.originalBytes) {
        toast.success(
          t(
            "app.toast.compressed",
            formatBytesShort(prepared.originalBytes),
            formatBytesShort(prepared.compressedBytes),
          ),
        );
      }
      setUploadProgress({ phase: "sending", percent: 100 });
      const res = (await analyze({
        data: {
          imageBase64: prepared.dataUrl,
          goal,
          environment: getStripeEnvironment(),
          locale,
          jurisdiction,
          userNotes: userNotes.trim() || undefined,
          userActions: userActions.length > 0 ? userActions : undefined,
        },
      })) as Result;
      setResult(res);
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : t("app.toast.analyze_error"));
    } finally {
      setLoading(false);
      setUploadProgress(null);
    }
  }

  const scoreDelta = result?.score != null && previous?.score != null
    ? result.score - previous.score
    : null;

  return (
    <div className="min-h-screen bg-background font-sans text-foreground" style={{ fontFamily: "var(--font-sans)" }}>
      {showConsent && (
        <GdprConsentModal
          onCancel={() => { setShowConsent(false); setPendingFile(null); }}
          onAccept={(c) => {
            setConsent(c);
            setShowConsent(false);
            const f = pendingFile; setPendingFile(null);
            if (f) onFile(f);
          }}
        />
      )}
      <Header />

      <section className="max-w-5xl mx-auto px-6 py-16">
        <div className="mb-10 text-center">
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-accent">{t("quick.kicker")}</span>
          <h1 className="font-display font-bold text-4xl md:text-5xl mt-2 mb-3 tracking-tight" style={{ fontFamily: "var(--font-display)" }}>
            {t("quick.title")}
          </h1>
          <p className="text-muted-foreground max-w-xl mx-auto">{t("quick.subtitle")}</p>
        </div>

        {!result && (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-10">
              {GOAL_IDS.map((g) => (
                <button
                  key={g}
                  onClick={() => setGoal(g)}
                  className={`text-left p-4 rounded-2xl border transition-all ${goal === g ? "border-accent bg-accent/5 ring-1 ring-accent" : "border-foreground/10 hover:border-foreground/30"}`}
                >
                  <p className="font-bold text-sm mb-0.5">{t(`app.goal.${g}`)}</p>
                  <p className="text-xs text-muted-foreground">{t(`app.goal.${g}.desc`)}</p>
                </button>
              ))}
            </div>

            <label className="block aspect-[16/10] md:aspect-[16/9] max-w-2xl mx-auto rounded-3xl border-2 border-dashed border-foreground/15 hover:border-accent/60 hover:bg-accent/5 transition-all cursor-pointer flex flex-col items-center justify-center text-center p-8 relative overflow-hidden">
              {preview ? (
                <img src={preview} alt={t("app.upload.alt")} className="absolute inset-0 w-full h-full object-cover" />
              ) : (
                <div className="m-auto">
                  <div className="size-14 rounded-full bg-accent/10 grid place-items-center mb-4 mx-auto">
                    <div className="size-6 rounded-full border-2 border-accent" />
                  </div>
                  <p className="font-semibold mb-1">{t("quick.upload.prompt")}</p>
                  <p className="text-xs text-muted-foreground">{t("app.upload.specs")}</p>
                </div>
              )}
              <input
                type="file" accept="image/*" capture="user"
                className="hidden" disabled={!authed || loading}
                onChange={(e) => e.target.files?.[0] && onFile(e.target.files[0])}
              />
            </label>

            <div className="max-w-2xl mx-auto mt-6">
              <NotesAndActionsField
                notes={userNotes}
                onNotesChange={setUserNotes}
                actions={userActions}
                onActionsChange={setUserActions}
                disabled={loading}
              />
            </div>

            {loading && (
              <div className="mt-8 text-center max-w-xs mx-auto">
                <div className="size-3 bg-accent rounded-full animate-pulse mx-auto mb-3" />
                <p className="text-sm text-muted-foreground mb-3">
                  {uploadProgress
                    ? t(`app.upload.phase.${uploadProgress.phase}`)
                    : t("app.result.loading")}
                </p>
                <div className="h-1.5 w-full bg-foreground/10 rounded-full overflow-hidden">
                  <div
                    className={`h-full bg-accent transition-all duration-200 ${
                      uploadProgress?.phase === "sending" ? "animate-pulse" : ""
                    }`}
                    style={{ width: `${uploadProgress?.percent ?? 100}%` }}
                  />
                </div>
                {uploadProgress && (
                  <p className="text-[10px] text-muted-foreground mt-2 tabular-nums">
                    {Math.round(uploadProgress.percent)}%
                  </p>
                )}
              </div>
            )}
          </>
        )}

        {result && (
          <>
            {/* Before / After */}
            <div className="grid md:grid-cols-2 gap-6 mb-10">
              <div className="rounded-3xl border border-foreground/10 bg-muted p-5">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground">{t("quick.before")}</span>
                  {previous?.score != null && <span className="text-xs font-bold">{previous.score}/100</span>}
                </div>
                <div className="aspect-[4/5] rounded-2xl bg-foreground/5 grid place-items-center text-xs text-muted-foreground text-center p-4">
                  {previous ? new Date(previous.createdAt).toLocaleDateString(locale) : t("quick.no_previous")}
                </div>
                {previous?.summary && (
                  <p className="text-xs text-muted-foreground mt-3 line-clamp-3">{previous.summary}</p>
                )}
              </div>
              <div className="rounded-3xl border border-accent/30 bg-accent/5 p-5">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-[11px] font-bold uppercase tracking-widest text-accent">{t("quick.after")}</span>
                  <div className="flex items-center gap-2">
                    {result.score != null && <span className="text-xs font-bold">{result.score}/100</span>}
                    {scoreDelta != null && (
                      <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full ${scoreDelta > 0 ? "bg-accent text-accent-foreground" : "bg-foreground/10 text-muted-foreground"}`}>
                        {scoreDelta > 0 ? "+" : ""}{scoreDelta}
                      </span>
                    )}
                  </div>
                </div>
                {preview && (
                  <img src={preview} alt={t("quick.after")} className="aspect-[4/5] w-full object-cover rounded-2xl" />
                )}
                <p className="text-xs text-foreground/80 mt-3">{result.summary}</p>
              </div>
            </div>

            {/* Metrics */}
            {result.metrics.length > 0 && (
              <div className="rounded-3xl border border-foreground/10 p-6 mb-8">
                <h2 className="font-display font-bold text-xl mb-4" style={{ fontFamily: "var(--font-display)" }}>{t("quick.metrics")}</h2>
                <div className="space-y-3">
                  {result.metrics.map((m) => (
                    <div key={m.label}>
                      <div className="flex justify-between text-[11px] font-bold uppercase tracking-wider mb-1">
                        <span>{m.label}</span>
                        <span className="text-accent">{m.delta}</span>
                      </div>
                      <div className="h-1.5 w-full bg-foreground/5 rounded-full overflow-hidden">
                        <div className="h-full bg-accent" style={{ width: `${Math.max(0, Math.min(100, m.score))}%` }} />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Tips */}
            {result.tips.length > 0 && (
              <div className="bg-primary text-primary-foreground rounded-3xl p-8 mb-8">
                <span className="text-xs font-bold uppercase tracking-[0.2em] text-accent">{t("quick.tips.kicker")}</span>
                <h2 className="font-display font-bold text-2xl mt-2 mb-5" style={{ fontFamily: "var(--font-display)" }}>{t("quick.tips.title")}</h2>
                <ul className="space-y-5">
                  {result.tips.slice(0, 5).map((tip, i) => (
                    <TipItem
                      key={i}
                      index={i}
                      tip={tip}
                      numberClassName="text-accent font-display text-2xl font-bold leading-none"
                      detailClassName="text-primary-foreground/70 text-xs leading-relaxed"
                      badgeClassName="text-[10px] uppercase tracking-wider bg-primary-foreground/10 text-primary-foreground/80 px-2 py-0.5 rounded-full"
                      disclaimerClassName="text-[11px] text-accent/90 mt-2 italic"
                    />
                  ))}
                </ul>
              </div>
            )}

            <div className="flex flex-wrap gap-3 justify-center">
              <button
                onClick={() => { setResult(null); setPreview(null); }}
                className="border border-foreground/15 px-6 py-3 rounded-full text-sm font-semibold hover:bg-foreground/5"
              >
                {t("quick.retry")}
              </button>
              <Link to="/progress" className="bg-foreground text-background px-6 py-3 rounded-full text-sm font-bold hover:opacity-90">
                {t("quick.see_progress")}
              </Link>
            </div>
          </>
        )}
      </section>
    </div>
  );
}
